/* ----------------------------------------------------------------------------
 * BEX "Patient Data Collection" visit form — 8 chapters (vue-formulate schemas)
 *
 * Reconstructed from the compiled bundle (chapter order + field names/types),
 * the LIVE app (labels, options, sections — dbi-research-production), and the
 * ground-truth stored shapes in firestore_backup.json. Data nesting confirmed:
 *   anthropometrics: flat (dateOfVisit,dateOfBirth,sex,weight,ethnicity,height)
 *   eating-drinking: drink{}, lastMeal{} (booleans), timeLast{}
 *   smoking:    ARRAY [{habits, tobacco:[{packYears,yearEnded,cigsPerDay}], cannabis:[{cannabisType}]}]
 *   medication: ARRAY [{drugName,activeSubstance,dosagePerIntake,intake*,lastIntake,startedDate}]
 *   morbidities/comorbidities: ARRAY [{morbidity}] / [{comorbidity}] + comments
 *   questionnaire: flat scores + single `date` + SF-36 subscores
 *   lung-function: spirometry{} ; breath-analysis: breathAnalysis{ positiveMode{}, negativeMode{} }
 *
 * Chapters are addressed by numeric index (route param :chapter), matching the
 * original. The non-BEX projects use *-shared-v2 variants — TODO separately.
 * Labels verbatim from the live app. (verify) flags = double-check on sign-off.
 * ------------------------------------------------------------------------- */
export const yesNo = { '': '', yes: 'yes', no: 'no' }

export const anthropometrics = [
  { type: 'date', name: 'dateOfVisit', label: 'Date of Visit' },
  { type: 'date', name: 'dateOfBirth', label: 'Date of Birth' },
  {
    type: 'select',
    name: 'sex',
    label: 'Sex',
    placeholder: 'Select',
    options: { '': '', male: 'male', female: 'female' },
  },
  {
    type: 'number',
    name: 'weight',
    label: 'Weight (kg)',
    placeholder: 'Weight (kg)',
    step: 'any',
    min: '0',
  },
  {
    type: 'select',
    name: 'ethnicity',
    label: 'Ethnicity',
    placeholder: 'Select',
    options: {
      '': '',
      white: 'White',
      indianAlaska: 'American Indian or Alaska Native',
      asian: 'Asian',
      black: 'Black or African American',
      hawaian: 'Native Hawaiian or other Pacific Islander',
    },
  },
  {
    type: 'number',
    name: 'height',
    label: 'Height (cm)',
    placeholder: 'Height (cm)',
    step: 'any',
    min: '0',
  },
]

export const eatingDrinking = [
  { component: 'h4', children: 'Eating & drinking habits' },
  {
    type: 'group',
    name: 'drink',
    children: [
      { type: 'select', name: 'alcohol', label: 'Alcohol', options: yesNo },
      { type: 'select', name: 'coffee', label: 'Coffee', options: yesNo },
      {
        type: 'select',
        name: 'caffeinatedDrink',
        label: 'Caffeinated drink',
        options: yesNo,
      },
    ],
  },
  {
    type: 'group',
    name: 'timeLast',
    children: [
      { type: 'datetime-local', name: 'coffee', label: 'Time of last coffee' },
      { type: 'datetime-local', name: 'meal', label: 'Time of last meal' },
    ],
  },
  { component: 'h4', children: 'Last meal' },
  {
    type: 'group',
    name: 'lastMeal',
    children: [
      { type: 'checkbox', name: 'small', label: 'Small' },
      { type: 'checkbox', name: 'medium', label: 'Medium' },
      { type: 'checkbox', name: 'big', label: 'Big' },
      { type: 'checkbox', name: 'fatty', label: 'Fatty' },
      {
        type: 'checkbox',
        name: 'carbohydrateBased',
        label: 'Carbohydrate-based',
      },
      { type: 'checkbox', name: 'proteinBased', label: 'Protein-based' },
    ],
  },
]

// NOTE: smoking sub-fields (tobacco/cannabis) are conditional on `habits` in the
// original (custom component). Modelled here as always-visible repeatable groups
// so the stored array shape matches; conditional display is a follow-up refinement.
const smoking = [
  {
    type: 'group',
    name: 'smoking',
    repeatable: false,
    children: [
      {
        type: 'select',
        name: 'habits',
        label: 'Smoking habits',
        options: {
          '': '',
          neverSmoker: 'Never-smoker',
          smoker: 'Smoker',
          exSmoker: 'Ex-smoker',
        },
      },
      {
        type: 'group',
        name: 'tobacco',
        repeatable: true,
        'add-label': '+ Add tobacco',
        children: [
          {
            type: 'number',
            name: 'cigsPerDay',
            label: 'Cigarettes per day',
            min: '0',
            value: '0',
          },
          {
            type: 'number',
            name: 'yearStarted',
            label: 'Year started',
            min: '0',
          },
          { type: 'number', name: 'yearEnded', label: 'Year ended', min: '0' },
          {
            // Manual vs. auto pack-years. "Enter manually" (default) leaves whatever
            // the operator types untouched — the usual case, and the only option for
            // the atypical smoking histories the formula can't capture. "Auto-calculate"
            // recomputes packYears from cigsPerDay + the smoking span (see
            // ClinicalForm.recomputePackYears). This flag is UI-only: it is inferred on
            // load (stored value matches the formula ⇒ auto, otherwise manual) and
            // stripped before save, so no new key is written to the database.
            type: 'radio',
            name: 'packYearsMode',
            label: 'Pack-years',
            value: 'manual',
            options: { auto: 'Auto-calculate', manual: 'Enter manually' },
          },
          {
            // Recomputed only while the row is in "auto" mode and only after the
            // operator edits (never on load), so stored historical values are
            // preserved. Pack-year = (cigs/day ÷ 20) × years smoked, where
            // years = (yearEnded or current year) − yearStarted.
            type: 'number',
            name: 'packYears',
            label: 'Pack years',
            help:
              'Type the pack-years directly, or switch to “Auto-calculate” above to derive them as (cigarettes per day ÷ 20) × (year ended − year started).',
            min: '0',
          },
        ],
      },
      {
        type: 'group',
        name: 'eCigarette',
        repeatable: true,
        'add-label': '+ Add e-cigarette',
        children: [
          {
            type: 'text',
            name: 'type',
            label: 'Type',
            placeholder: 'e.g. IQOS, Vape',
          },
          {
            type: 'number',
            name: 'numberOfBottlesPM',
            label: 'Bottles per month',
            min: '0',
          },
          {
            type: 'number',
            name: 'amountPerBottle',
            label: 'Amount per bottle (ml)',
            min: '0',
            step: 'any',
          },
          {
            type: 'number',
            name: 'yearStarted',
            label: 'Year started',
            min: '0',
          },
          { type: 'number', name: 'yearEnded', label: 'Year ended', min: '0' },
        ],
      },
      {
        type: 'group',
        name: 'cannabis',
        repeatable: true,
        'add-label': '+ Add cannabis',
        children: [
          {
            type: 'select',
            name: 'cannabisType',
            label: 'Cannabis type',
            placeholder: 'Select',
            options: { '': '', Marijuana: 'Marijuana', Hashish: 'Hashish' },
          },
          {
            type: 'number',
            name: 'amountOfCannabis',
            label: 'Amount (g/month)',
            min: '0',
            step: 'any',
          },
          {
            type: 'number',
            name: 'yearStarted',
            label: 'Year started',
            min: '0',
          },
          { type: 'number', name: 'yearEnded', label: 'Year ended', min: '0' },
        ],
      },
    ],
  },
  // Free-text notes. Reuses the visit-level `comments` field (also shown in the
  // Morbidities chapter) — no new database key is introduced.
  { type: 'textarea', name: 'comments', label: 'Comments' },
]

export const medication = [
  {
    type: 'group',
    name: 'medication',
    repeatable: true,
    'add-label': '+ Add medication',
    children: [
      {
        type: 'drug',
        name: 'drugName',
        label: 'Drug (Trade Name)',
        placeholder: 'Drug (Trade Name)',
      },
      {
        type: 'text',
        name: 'activeSubstance',
        label: 'Active Substance',
        placeholder: 'Active Substance',
      },
      {
        type: 'number',
        name: 'dosagePerIntake',
        label: 'Dosage per Intake (mg)',
        placeholder: 'Dosage',
        min: '0',
        step: 'any',
      },
      {
        type: 'number',
        name: 'intakeMorning',
        label: 'Morning',
        help: 'Morning',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeMidday',
        label: 'Midday',
        help: 'Midday',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeEvening',
        label: 'Evening',
        help: 'Evening',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeNight',
        label: 'Night',
        help: 'Night',
        min: '0',
        value: '0',
      },
      { type: 'datetime-local', name: 'lastIntake', label: 'Last intake' },
      { type: 'date', name: 'startedDate', label: 'Medication started' },
    ],
  },
]

export const morbidities = [
  {
    type: 'group',
    name: 'morbidities',
    repeatable: true,
    'add-label': '+ Add morbidity',
    children: [
      {
        type: 'icd',
        name: 'morbidity',
        label: 'Morbidity',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  {
    type: 'group',
    name: 'comorbidities',
    repeatable: true,
    'add-label': '+ Add comorbidity',
    children: [
      {
        type: 'icd',
        name: 'comorbidity',
        label: 'Comorbidity',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  { type: 'textarea', name: 'comments', label: 'Comments' },
]

// Scores are flat strings; a single shared `date` accompanies them (verify).
const questionnaire = [
  { component: 'h4', children: 'Scores' },
  { type: 'date', name: 'date', label: 'Date' },
  { type: 'text', name: 'CATScore', label: 'CAT score' },
  { type: 'text', name: 'hassScore', label: 'HASS score' },
  { type: 'text', name: 'actScore', label: 'ACT score' },
  { type: 'text', name: 'mMRCscore', label: 'mMRC score' },
  { type: 'text', name: 'nyhaScore', label: 'NYHA score' },
  { type: 'text', name: 'essScore', label: 'ESS score' },
  { component: 'h4', children: 'SF-36' },
  {
    type: 'text',
    name: 'physicalHealthCS',
    label: 'Physical health component score (PCS)',
  },
  {
    type: 'text',
    name: 'mentalHealthCS',
    label: 'Mental health component score (MCS)',
  },
  { type: 'text', name: 'physicalFunctioning', label: 'Physical functioning' },
  {
    type: 'text',
    name: 'roleLimitationsPhy',
    label: 'Role limitations (physical)',
  },
  {
    type: 'text',
    name: 'roleLimitationsEmo',
    label: 'Role limitations (emotional)',
  },
  { type: 'text', name: 'energyOrFatigue', label: 'Energy/Fatigue' },
  { type: 'text', name: 'emotionalWellBeing', label: 'Emotional well-being' },
  { type: 'text', name: 'socialFunctioning', label: 'Social functioning' },
  { type: 'text', name: 'pain', label: 'Pain' },
  { type: 'text', name: 'genHealth', label: 'General health' },
]

export const lungFunction = [
  {
    type: 'group',
    name: 'spirometry',
    children: [
      { type: 'date', name: 'date', label: 'Date of spirometry' },
      { type: 'text', name: 'FEV1-L', label: 'FEV1 (l)' },
      { type: 'text', name: 'FEV1-percent', label: 'FEV1 (%)' },
      { type: 'text', name: 'FVC-L', label: 'FVC (l)' },
      { type: 'text', name: 'FVC-percent', label: 'FVC (%)' },
      { type: 'text', name: 'FEVtoFVC', label: 'FEV1/FVC (%)' },
      { type: 'text', name: 'PEF', label: 'Peak Expiratory Flow (PEF) (l/s)' },
      { type: 'text', name: 'TLC-L', label: 'Total Lung Capacity (TLC) (l)' },
      {
        type: 'text',
        name: 'TLC-percent',
        label: 'Total Lung Capacity (TLC) (%)',
      },
      { type: 'text', name: 'RV-L', label: 'Residual Volume (RV) (l)' },
      { type: 'text', name: 'RV-percent', label: 'Residual Volume (RV) (%)' },
      { type: 'text', name: 'RVtoTLC', label: 'RV/TLC (%)' },
      { type: 'text', name: 'DLCO', label: 'DLCO (mmol/kPa/min)' },
      { type: 'text', name: 'DLCO-percent', label: 'DLCO (%)' },
      { type: 'text', name: 'FenO', label: 'FeNO (ppb)' },
    ],
  },
]

const breathChecklist = [
  { name: 'consumeAlcohol', label: 'Consume alcohol' },
  { name: 'hadDrinkNoWater', label: 'Have a drink other than water' },
  { name: 'smokeConsumeTobacco', label: 'Smoke or consume tobacco' },
  { name: 'chewGumCandy', label: 'Chew gum or candy' },
  { name: 'brushTeeth', label: 'Brush your teeth' },
  { name: 'consumeCaffeinatedDrink', label: 'Consume a caffeinated drink' },
  { name: 'useInhalants', label: 'Use inhalants' },
  {
    name: 'acuteCold6Weeks',
    label: 'Have an acute cold (in the past 6 weeks)',
  },
  { name: 'cosmeticFace', label: 'Apply cosmetics to your face' },
].map(({ name, label }) => ({ type: 'checkbox', name, label }))

const breathMode = name => ({
  type: 'group',
  name,
  children: [
    { type: 'text', name: 'capPosition', label: 'Cap Position' },
    { type: 'text', name: 'sprayBaseline', label: 'Spray current (baseline)' },
    {
      type: 'text',
      name: 'sprayMeasurement',
      label: 'Spray current (measurement)',
    },
    {
      type: 'select',
      name: 'exhalationQuality',
      label: 'Exhalation quality',
      options: { '': '', good: 'good', ok: 'ok', bad: 'bad' },
    },
    {
      type: 'text',
      name: 'exhalationDescription',
      label: 'Description of exhalations',
    },
  ],
})

export const breathAnalysis = [
  {
    type: 'group',
    name: 'breathAnalysis',
    children: [
      { type: 'datetime-local', name: 'date', label: 'Date' },
      {
        type: 'select',
        name: 'operatorInCharge',
        label: 'Operator in charge',
        options: {
          '': '',
          sarah: 'sarahute.basler@usz.ch',
          kimberly: 'kimberly.nitzsche@usz.ch',
          selvete: 'selvete.bajrami@usz.ch',
          kai: 'kai.fricke@usz.ch',
          annina: 'anninakohler@hotmail.com',
          yassin: 'yassin.rhouma@usz.ch',
          felix: 'felix.schmidt@usz.ch',
        },
      },
      { component: 'h4', children: 'Did you in the past hour…?' },
      ...breathChecklist,
      {
        type: 'text',
        name: 'explanation',
        label: 'If question is ticked, explain further',
      },
      {
        type: 'select',
        name: 'mouthRinsed',
        label: 'Mouth rinsed',
        options: yesNo,
      },
      {
        type: 'text',
        name: 'capilliarySupplier',
        label: 'Capillary (Supplier/LOT)',
      },
      { component: 'h4', children: 'Positive Mode' },
      breathMode('positiveMode'),
      { component: 'h4', children: 'Negative Mode' },
      breathMode('negativeMode'),
    ],
  },
]

export default {
  chapters: [
    {
      key: 'anthropometrics',
      name: 'Anthropometrics',
      schema: anthropometrics,
    },
    {
      key: 'eatingDrinking',
      name: 'Eating & drinking habits',
      schema: eatingDrinking,
    },
    { key: 'smokingHistory', name: 'Smoking history', schema: smoking },
    { key: 'medication', name: 'Medication', schema: medication },
    { key: 'morbidities', name: 'Morbidities', schema: morbidities },
    { key: 'questionnaire', name: 'Questionnaire', schema: questionnaire },
    { key: 'lungFunction', name: 'Lung Function', schema: lungFunction },
    { key: 'breathAnalysis', name: 'Breath analysis', schema: breathAnalysis },
  ],
}
